import React, { useState, useEffect, useCallback } from 'react';
import s3ViewerService from '../services/s3ViewerService';
import './Admin.css';

const fmtSize = (n) => {
    if (n == null) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const FolderIcon = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
        <path d="M2 5.5A1.5 1.5 0 0 1 3.5 4h3l1.5 2h8A1.5 1.5 0 0 1 17.5 7.5v7A1.5 1.5 0 0 1 16 16H3.5A1.5 1.5 0 0 1 2 14.5v-9z" stroke="#4a6cf7" strokeWidth="1.4" />
    </svg>
);
const FileIcon = () => (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
        <path d="M5 2.5h6l4 4v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-14a1 1 0 0 1 1-1z" stroke="#6b7280" strokeWidth="1.4" />
        <path d="M11 2.5v4h4" stroke="#6b7280" strokeWidth="1.4" />
    </svg>
);
const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);

const rowStyle = { display: 'flex', alignItems: 'center', gap: 10, padding: '0.5rem 0.75rem', borderBottom: '1px solid #f3f4f6', fontSize: '0.9rem', background: '#fff' };
const clickRow = { ...rowStyle, cursor: 'pointer', flex: 1, border: 'none', textAlign: 'left', background: 'transparent' };
const crumbBtn = { border: 'none', background: 'transparent', color: '#4a6cf7', cursor: 'pointer', fontSize: '0.9rem', padding: 0 };
const iconBtn = { border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center' };

const S3Viewer = () => {
    const [bucket, setBucket] = useState(null);   // null = show bucket list
    const [prefix, setPrefix] = useState('');
    const [buckets, setBuckets] = useState([]);
    const [folders, setFolders] = useState([]);
    const [files, setFiles] = useState([]);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [file, setFile] = useState(null);
    const [copied, setCopied] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState(null); // null = not searching
    const [filter, setFilter] = useState('');                 // quick client-side filter of the current view
    const [busy, setBusy] = useState(false);
    const [createModal, setCreateModal] = useState(null);     // { type, name, content }
    const [deleteTarget, setDeleteTarget] = useState(null);   // { kind:'file'|'folder', key?, prefix?, name }

    const loadBuckets = useCallback(async () => {
        setLoading(true); setError(null);
        try { const res = await s3ViewerService.listBuckets(); setBuckets(res.buckets || []); }
        catch (e) { setError(e.response?.data?.message || e.message || 'Failed to list buckets'); }
        finally { setLoading(false); }
    }, []);

    const loadPrefix = useCallback(async (b, p) => {
        setLoading(true); setError(null); setFile(null);
        try {
            const res = await s3ViewerService.list(b, p);
            setFolders(res.folders || []); setFiles(res.files || []); setTruncated(!!res.truncated);
        } catch (e) { setError(e.response?.data?.message || e.message || 'Failed to list objects'); }
        finally { setLoading(false); }
    }, []);

    const reload = useCallback(() => {
        if (bucket === null) loadBuckets(); else loadPrefix(bucket, prefix);
    }, [bucket, prefix, loadBuckets, loadPrefix]);

    useEffect(() => { setSearchResults(null); setSearchTerm(''); setFilter(''); reload(); }, [bucket, prefix, reload]);

    const openFile = async (key, name) => {
        setFile({ key, name, loading: true });
        try { const res = await s3ViewerService.getObject(bucket, key); setFile({ name, ...res }); }
        catch (e) { setFile({ key, name, error: e.response?.data?.message || e.message || 'Failed to read object' }); }
    };

    const runSearch = async () => {
        const term = searchTerm.trim();
        if (!bucket || !term) { setSearchResults(null); return; }
        setLoading(true); setError(null);
        try { const res = await s3ViewerService.search(bucket, term, prefix); setSearchResults(res); }
        catch (e) { setError(e.response?.data?.message || e.message || 'Search failed'); }
        finally { setLoading(false); }
    };

    const doCreate = async () => {
        const name = (createModal.name || '').trim();
        if (!name) return;
        setBusy(true); setError(null);
        try {
            if (createModal.type === 'folder') await s3ViewerService.createFolder(bucket, prefix, name);
            else await s3ViewerService.createFile(bucket, prefix, name, createModal.content || '');
            setCreateModal(null); reload();
        } catch (e) { setError(e.response?.data?.message || e.message || 'Create failed'); }
        finally { setBusy(false); }
    };

    const doDelete = async () => {
        setBusy(true); setError(null);
        try {
            if (deleteTarget.kind === 'folder') await s3ViewerService.deletePrefix(bucket, deleteTarget.prefix);
            else await s3ViewerService.deleteObject(bucket, deleteTarget.key);
            setDeleteTarget(null);
            if (searchResults) runSearch(); else reload();
        } catch (e) { setError(e.response?.data?.message || e.message || 'Delete failed'); }
        finally { setBusy(false); }
    };

    // Quick client-side filter of the CURRENT view (buckets, or the folders/files at this level). This is
    // instant and local; the "Search" box below does a deep recursive key search on the server.
    const q = filter.trim().toLowerCase();
    const shownBuckets = q ? buckets.filter(b => b.name.toLowerCase().includes(q)) : buckets;
    const shownFolders = q ? folders.filter(f => f.name.toLowerCase().includes(q)) : folders;
    const shownFiles = q ? files.filter(f => f.name.toLowerCase().includes(q)) : files;

    const filterInput = (
        <input
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder={bucket === null ? 'Filter buckets…' : 'Filter this folder…'}
            style={{ flex: 1, padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }}
        />
    );

    // Breadcrumb: All Buckets / <bucket> / <prefix parts…>
    const crumbs = [];
    if (bucket) {
        crumbs.push({ label: bucket, onClick: () => setPrefix('') });
        let acc = '';
        prefix.split('/').filter(Boolean).forEach(part => { acc += part + '/'; const t = acc; crumbs.push({ label: part, onClick: () => setPrefix(t) }); });
    }

    return (
        <div className="admin-container">
            <div className="admin-header">
                <h2>S3 Viewer</h2>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" onClick={reload} disabled={loading}>
                        {loading ? 'Refreshing…' : '↻ Refresh'}
                    </button>
                    {bucket && (
                        <>
                            <button className="btn btn-secondary" onClick={() => setCreateModal({ type: 'folder', name: '' })}>+ Folder</button>
                            <button className="btn btn-secondary" onClick={() => setCreateModal({ type: 'file', name: '', content: '' })}>+ File</button>
                        </>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', margin: '4px 0 12px' }}>
                <button style={crumbBtn} onClick={() => { setBucket(null); setPrefix(''); setFile(null); }}>All Buckets</button>
                {crumbs.map((c, i) => (
                    <React.Fragment key={i}><span style={{ color: '#9ca3af' }}>/</span><button style={crumbBtn} onClick={c.onClick}>{c.label}</button></React.Fragment>
                ))}
            </div>

            {bucket === null ? (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    {filterInput}
                </div>
            ) : (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    {filterInput}
                    <input
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && runSearch()}
                        placeholder={`Search keys under ${prefix || bucket + '/'}…`}
                        style={{ flex: 1, padding: '0.45rem 0.6rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }}
                    />
                    <button className="btn btn-primary" onClick={runSearch} disabled={!searchTerm.trim()}>Search</button>
                    {searchResults && <button className="btn btn-secondary" onClick={() => { setSearchResults(null); setSearchTerm(''); }}>Clear</button>}
                </div>
            )}

            {error && <div className="error">{error}</div>}

            {loading ? (
                <div className="loading">Loading…</div>
            ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                    {bucket === null ? (
                        shownBuckets.length === 0
                            ? <div style={{ padding: '1rem', color: '#6b7280' }}>{buckets.length ? 'No buckets match the filter.' : 'No buckets.'}</div>
                            : shownBuckets.map(b => (
                                <div key={b.name} style={rowStyle}>
                                    <button style={clickRow} onClick={() => { setPrefix(''); setBucket(b.name); }}>
                                        <FolderIcon /><span style={{ fontWeight: 600 }}>{b.name}</span>
                                    </button>
                                </div>
                            ))
                    ) : searchResults ? (
                        searchResults.files.length === 0
                            ? <div style={{ padding: '1rem', color: '#6b7280' }}>No matches.</div>
                            : (<>
                                {searchResults.files.map(f => (
                                    <div key={f.key} style={rowStyle}>
                                        <button style={clickRow} onClick={() => openFile(f.key, f.key)}>
                                            <FileIcon /><span style={{ flex: 1, wordBreak: 'break-all' }}>{f.key}</span>
                                            <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{fmtSize(f.size)}</span>
                                        </button>
                                        <button style={iconBtn} title="Delete" onClick={() => setDeleteTarget({ kind: 'file', key: f.key, name: f.key })}><TrashIcon /></button>
                                    </div>
                                ))}
                                {searchResults.truncated && <div style={{ padding: '0.5rem 0.75rem', color: '#b45309', fontSize: '0.8rem' }}>Showing the first {searchResults.files.length} matches.</div>}
                            </>)
                    ) : (
                        <>
                            {shownFolders.map(f => (
                                <div key={f.prefix} style={rowStyle}>
                                    <button style={clickRow} onClick={() => setPrefix(f.prefix)}><FolderIcon /><span>{f.name}/</span></button>
                                    <button style={iconBtn} title="Delete folder" onClick={() => setDeleteTarget({ kind: 'folder', prefix: f.prefix, name: f.name + '/' })}><TrashIcon /></button>
                                </div>
                            ))}
                            {shownFiles.map(f => (
                                <div key={f.key} style={rowStyle}>
                                    <button style={clickRow} onClick={() => openFile(f.key, f.name)}>
                                        <FileIcon /><span style={{ flex: 1 }}>{f.name}</span>
                                        <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{fmtSize(f.size)}</span>
                                        <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{f.lastModified ? new Date(f.lastModified).toLocaleString() : ''}</span>
                                    </button>
                                    <button style={iconBtn} title="Delete" onClick={() => setDeleteTarget({ kind: 'file', key: f.key, name: f.name })}><TrashIcon /></button>
                                </div>
                            ))}
                            {shownFolders.length === 0 && shownFiles.length === 0 && (
                                <div style={{ padding: '1rem', color: '#6b7280' }}>{(folders.length || files.length) ? 'Nothing matches the filter.' : 'Empty.'}</div>
                            )}
                            {truncated && <div style={{ padding: '0.5rem 0.75rem', color: '#b45309', fontSize: '0.8rem' }}>Showing the first 1000 items.</div>}
                        </>
                    )}
                </div>
            )}

            {/* File view */}
            {file && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setFile(null)}>
                    <div style={{ background: '#fff', borderRadius: 10, width: 'min(900px, 92vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 10 }}>
                            <strong style={{ flex: 1, wordBreak: 'break-all', fontSize: '0.9rem' }}>{file.name || file.key}</strong>
                            {file.size != null && <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{fmtSize(file.size)}{file.contentType ? ` · ${file.contentType}` : ''}</span>}
                            {file.body != null && file.encoding !== 'base64' && (
                                <button
                                    className="btn btn-secondary"
                                    onClick={async () => {
                                        try {
                                            await navigator.clipboard.writeText(file.body || '');
                                            setCopied(true);
                                            setTimeout(() => setCopied(false), 1500);
                                        } catch (e) { /* clipboard unavailable */ }
                                    }}
                                >
                                    {copied ? '✓ Copied' : 'Copy'}
                                </button>
                            )}
                            <button className="btn btn-secondary" onClick={() => { setFile(null); setCopied(false); }}>Close</button>
                        </div>
                        <div style={{ overflow: 'auto', padding: '1rem' }}>
                            {file.loading ? <div className="loading">Loading…</div>
                                : file.error ? <div className="error">{file.error}</div>
                                : file.note ? <div style={{ color: '#6b7280' }}>{file.note}</div>
                                : file.encoding === 'base64' ? <div style={{ color: '#6b7280' }}>Binary file ({fmtSize(file.size)}) — not previewable as text.</div>
                                : <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>{file.body}</pre>}
                        </div>
                    </div>
                </div>
            )}

            {/* Create modal */}
            {createModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }} onClick={() => !busy && setCreateModal(null)}>
                    <div style={{ background: '#fff', borderRadius: 10, width: 'min(560px, 92vw)', padding: '1.25rem' }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>New {createModal.type === 'folder' ? 'Folder' : 'File'}</h3>
                        <p style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: 0 }}>In <code>{bucket}/{prefix}</code></p>
                        <div className="form-group">
                            <label>Name</label>
                            <input autoFocus value={createModal.name} onChange={e => setCreateModal(m => ({ ...m, name: e.target.value }))} placeholder={createModal.type === 'folder' ? 'folder-name' : 'file.txt'} />
                        </div>
                        {createModal.type === 'file' && (
                            <div className="form-group">
                                <label>Content</label>
                                <textarea rows={8} value={createModal.content} onChange={e => setCreateModal(m => ({ ...m, content: e.target.value }))} style={{ width: '100%', fontFamily: 'monospace', fontSize: '0.8rem' }} />
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                            <button className="btn btn-secondary" onClick={() => setCreateModal(null)} disabled={busy}>Cancel</button>
                            <button className="btn btn-primary" onClick={doCreate} disabled={busy || !createModal.name.trim()}>{busy ? 'Creating…' : 'Create'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete confirm */}
            {deleteTarget && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }} onClick={() => !busy && setDeleteTarget(null)}>
                    <div style={{ background: '#fff', borderRadius: 10, width: 'min(520px, 92vw)', padding: '1.25rem' }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0 }}>Delete {deleteTarget.kind}</h3>
                        <p style={{ wordBreak: 'break-all' }}>
                            Delete <strong>{deleteTarget.name}</strong> from <code>{bucket}</code>?
                            {deleteTarget.kind === 'folder' && <><br /><span style={{ color: '#dc2626' }}>This deletes every object under the folder. This cannot be undone.</span></>}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                            <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)} disabled={busy}>Cancel</button>
                            <button className="btn btn-danger" onClick={doDelete} disabled={busy}>{busy ? 'Deleting…' : 'Delete'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default S3Viewer;
